// ContactModal.js
import React, { useState, useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import { getTranslations } from "./components/translations";

const getImageUrl = (thumbnail) => {
  if (thumbnail && thumbnail.startsWith("#")) return thumbnail;
  if (thumbnail && (thumbnail.startsWith("http") || thumbnail.startsWith("//")))
    return thumbnail;
  if (thumbnail) {
    const cleanPath = thumbnail.replace(/^\.\//, "");
    return cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
  }
  return "#";
};

const ContactModal = ({
  isOpen,
  onClose,
  selectedProduct,
  selectedMaterials = {},
  viewerMaterials = [],
  language = "it",
}) => {
  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    azienda: "",
    noteAggiuntive: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Stati per screenshot e PDF
  const [screenshotBase64, setScreenshotBase64] = useState(null);
  const [screenshotDataUrl, setScreenshotDataUrl] = useState(null);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [isGeneratingAttachments, setIsGeneratingAttachments] = useState(false);

  const translations = getTranslations(language);

  const getVisibleSelectedMaterials = () => {
    if (!selectedMaterials || !viewerMaterials) return {};
    const visibleMaterials = {};

    const visibleMaterialTypes = viewerMaterials
      .filter((mat) => mat.variations.length >= 2)
      .map((mat) => mat.technicalName);

    Object.entries(selectedMaterials).forEach(([type, material]) => {
      if (visibleMaterialTypes.includes(type)) {
        visibleMaterials[type] = material;
      }
    });

    return visibleMaterials;
  };

  const getTypeLabel = (type) => {
    if (!type) return "Altro";

    if (selectedProduct?.materials?.[type]?.label) {
      return selectedProduct.materials[type].label;
    }

    if (selectedProduct?._familyDefaults?.defaultMaterials?.[type]?.label) {
      return selectedProduct._familyDefaults.defaultMaterials[type].label;
    }

    if (
      translations.materialTypes &&
      translations.materialTypes[type.toLowerCase()]
    ) {
      return translations.materialTypes[type.toLowerCase()];
    }

    return (
      type.charAt(0).toUpperCase() + type.slice(1).replace(/([A-Z])/g, " $1")
    );
  };

  const generateProductSummary = () => {
    if (!selectedProduct) return "";

    let summary = `${translations.contactModal.productDetails}:\n`;
    summary += `• ${translations.model}: ${selectedProduct.name}\n`;
    summary += `• ${translations.code}: ${selectedProduct.code}\n`;
    if (selectedProduct.family || selectedProduct._family) {
      summary += `• ${translations.family}: ${
        selectedProduct.family || selectedProduct._family
      }\n`;
    }

    const visibleMaterials = getVisibleSelectedMaterials();
    if (Object.keys(visibleMaterials).length > 0) {
      summary += `\n${translations.contactModal.selectedMaterials}:\n`;
      Object.entries(visibleMaterials).forEach(([type, material]) => {
        const typeLabel = getTypeLabel(type);
        summary += `• ${typeLabel}: ${material.name}\n`;
      });
    }

    summary += `\n${translations.priceInfo.estimate}:\n`;
    summary += `• ${translations.priceInfo.startingFrom}: €${
      selectedProduct.price?.toLocaleString() || "N/A"
    }\n`;

    summary += `\n${translations.priceInfo.contactInfo}\n`;
    summary += `• Tel: ${translations.priceInfo.phone}\n`;
    summary += `• Email: ${translations.priceInfo.emailSales}\n`;
    summary += `• Marketing: ${translations.priceInfo.emailMarketing}\n`;

    return summary;
  };

  // Email cliente tradotta
  const generateCustomerEmailHTML = (data) => {
    const { personalData } = data;
    const t = translations.customerEmail;

    return `<!DOCTYPE html>
<html lang="${language}">
<head>
  <meta charset="UTF-8">
  <title>${t.subject}</title>
</head>
<body style="font-family: Arial, Helvetica, sans-serif; line-height: 1.6; color: #333;">
  <div style="max-width:600px;margin:0 auto;background:#ffffff;border:1px solid #ddd;">
    <div style="background:#000;padding:20px;text-align:center;">
      <h1 style="color:#fff;margin:0;">Dieffebi</h1>
    </div>
    <div style="padding:30px;">
      <h2 style="margin-top:0;">${t.subject}</h2>
      <p>${t.greeting} <strong>${personalData.nome} ${
      personalData.cognome
    }</strong>,</p>
      <p>${t.thankYouIntro}</p>
      <p>${t.dataSummaryTitle}</p>
      <div style="background:#f9f9f9;border-left:4px solid #000;padding:15px;margin:20px 0;">
        <p><strong>${t.nameLabel}</strong> ${personalData.nome} ${
      personalData.cognome
    }</p>
        <p><strong>${t.emailLabel}</strong> ${personalData.email}</p>
        <p><strong>${t.phoneLabel}</strong> ${
      personalData.telefono || t.notProvided
    }</p>
        <p><strong>${t.companyLabel}</strong> ${
      personalData.azienda || t.notProvidedFemale
    }</p>
      </div>
      <div style="background:#fffbea;border:1px solid #ffeaa7;padding:15px;margin:20px 0;border-radius:4px;">
        <p style="margin:0;">
          📎 <strong>${t.attachmentNotice}</strong>, ${t.attachmentText}
        </p>
      </div>
      <p>${t.nextSteps}</p>
      <p>${t.contactPrompt}</p>
      <p style="margin-top:30px;">${t.closing}<br><strong>${
      t.teamSignature
    }</strong></p>
    </div>
  </div>
</body>
</html>`;
  };

  // Email interna sempre in italiano
  const generateInternalEmailHTML = (data) => {
    const { personalData, productData, selectedMaterials, pricing } = data;

    let materialsHTML = "";
    if (Object.keys(selectedMaterials).length > 0) {
      materialsHTML = "<h3>Materiali selezionati</h3><ul>";
      Object.entries(selectedMaterials).forEach(([type, material]) => {
        const typeLabel = getTypeLabel(type);
        materialsHTML += `<li><strong>${typeLabel}:</strong> ${material.name}`;
        if (material.collection) materialsHTML += ` (${material.collection})`;
        materialsHTML += "</li>";
      });
      materialsHTML += "</ul>";
    }

    return `<!DOCTYPE html>
<html lang="it">
<head>
  <meta charset="UTF-8">
  <title>Nuova richiesta configuratore</title>
</head>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color:#333;">
  <div style="max-width:800px;margin:0 auto;padding:20px;">
    <h1>Nuova richiesta configuratore</h1>
    <p><strong>Lingua richiesta:</strong> ${language.toUpperCase()}</p>
    <p>Ricevuta il ${new Date().toLocaleString("it-IT")}</p>

    <h3>Dati cliente</h3>
    <table style="width:100%;border-collapse:collapse;">
      <tr><td style="font-weight:bold;width:200px;">Nome e Cognome:</td><td>${
        personalData.nome
      } ${personalData.cognome}</td></tr>
      <tr><td style="font-weight:bold;">Email:</td><td>${
        personalData.email
      }</td></tr>
      <tr><td style="font-weight:bold;">Telefono:</td><td>${
        personalData.telefono || "Non fornito"
      }</td></tr>
      <tr><td style="font-weight:bold;">Azienda:</td><td>${
        personalData.azienda || "Non fornita"
      }</td></tr>
    </table>

    <h3>Dettagli prodotto</h3>
    <table style="width:100%;border-collapse:collapse;">
      <tr><td style="font-weight:bold;width:200px;">Nome:</td><td>${
        productData.name
      }</td></tr>
      <tr><td style="font-weight:bold;">Codice:</td><td>${
        productData.code
      }</td></tr>
      <tr><td style="font-weight:bold;">Famiglia:</td><td>${
        productData.family || "N/A"
      }</td></tr>
      <tr><td style="font-weight:bold;">Categoria:</td><td>${
        productData.category || "N/A"
      }</td></tr>
      <tr><td style="font-weight:bold;">Prezzo base:</td><td>€ ${pricing.basePrice.toLocaleString(
        "it-IT",
        { minimumFractionDigits: 2 }
      )}</td></tr>
    </table>

    ${materialsHTML}

    ${
      personalData.noteAggiuntive
        ? `<h3>Note aggiuntive</h3><p>${personalData.noteAggiuntive}</p>`
        : ""
    }

    <p><strong>Allegati:</strong> screenshot configurazione (JPG) + preventivo completo (PDF)</p>
  </div>
</body>
</html>`;
  };

  const getVisibleMaterialsByAccordion = () => {
    const visibleMaterials = getVisibleSelectedMaterials();
    if (Object.keys(visibleMaterials).length === 0) return {};

    const groups = {};
    Object.entries(visibleMaterials).forEach(([type, material]) => {
      const typeLabel = getTypeLabel(type);
      if (!groups[typeLabel]) groups[typeLabel] = [];
      groups[typeLabel].push({ type, material });
    });

    return groups;
  };

  // Genera screenshot e PDF quando il modal si apre
  useEffect(() => {
    if (isOpen) {
      setSubmitError("");
      setSubmitSuccess(false);
      setIsGeneratingAttachments(true);

      const generateAttachments = async () => {
        try {
          // Screenshot
          if (window.getConfiguratorScreenshotBase64) {
            const screenshotB64 =
              await window.getConfiguratorScreenshotBase64();
            if (screenshotB64) {
              setScreenshotBase64(screenshotB64);
              setScreenshotDataUrl(`data:image/jpeg;base64,${screenshotB64}`);
            }
          } else {
            console.warn("getConfiguratorScreenshotBase64 non disponibile");
          }

          // PDF
          if (window.getConfiguratorPDFBase64) {
            const pdfB64 = await window.getConfiguratorPDFBase64();
            if (pdfB64) {
              setPdfBase64(pdfB64);
            }
          } else {
            console.warn("getConfiguratorPDFBase64 non disponibile");
          }
        } catch (err) {
          console.error("Errore generazione allegati:", err);
        } finally {
          setIsGeneratingAttachments(false);
        }
      };

      generateAttachments();
    } else {
      // Reset quando si chiude
      setScreenshotBase64(null);
      setScreenshotDataUrl(null);
      setPdfBase64(null);
      setIsGeneratingAttachments(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Verifica che gli allegati siano pronti
    if (!screenshotBase64 || !pdfBase64) {
      setSubmitError("Attendere il caricamento degli allegati...");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const visibleSelectedMaterials = getVisibleSelectedMaterials();

      const payloadData = {
        language, // IMPORTANTE: lingua della richiesta
        personalData: { ...formData },
        productData: {
          name: selectedProduct?.name,
          code: selectedProduct?.code,
          family: selectedProduct?.family || selectedProduct?._family,
          category: selectedProduct?.category,
          basePrice: selectedProduct?.price,
        },
        selectedMaterials: visibleSelectedMaterials,
        pricing: {
          basePrice: selectedProduct?.price || 0,
          totalPrice: selectedProduct?.price || 0,
        },
        emailTemplates: {
          internal: {
            subject: `Nuova richiesta [${language.toUpperCase()}]: ${
              selectedProduct?.code
            }`,
            htmlBody: generateInternalEmailHTML({
              personalData: formData,
              productData: {
                name: selectedProduct?.name,
                code: selectedProduct?.code,
                family: selectedProduct?.family || selectedProduct?._family,
                category: selectedProduct?.category,
              },
              selectedMaterials: visibleSelectedMaterials,
              pricing: {
                basePrice: selectedProduct?.price || 0,
                totalPrice: selectedProduct?.price || 0,
              },
            }),
            textBody: generateProductSummary(),
          },
          customer: {
            subject: translations.customerEmail.subject,
            htmlBody: generateCustomerEmailHTML({ personalData: formData }),
            textBody:
              `${translations.customerEmail.greeting} ${formData.nome} ${formData.cognome},\n\n` +
              `${translations.customerEmail.thankYouIntro}\n\n` +
              `${translations.customerEmail.closing}\n${translations.customerEmail.teamSignature}`,
          },
        },
        attachments: {
          screenshot: {
            filename: `${selectedProduct?.code || "config"}_preview.jpg`,
            mimeType: "image/jpeg",
            base64: screenshotBase64,
          },
          pdf: {
            filename: `preventivo_${selectedProduct?.code || "config"}.pdf`,
            mimeType: "application/pdf",
            base64: pdfBase64,
          },
        },
        timestamp: new Date().toISOString(),
        currentUrl: window.location.href,
      };

      const response = await fetch(
        "https://backend.dieffebi.com/api/v1/configurator/submit",
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-API-Token": "aa18ee476125d0171d7d2c075f372df1",
          },
          body: JSON.stringify({
            type: "CONFIGURATOR_FORM_SUBMIT",
            data: payloadData,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error("API Error Response:", errorData);
        throw new Error(`Errore API: ${response.status}`);
      }

      setSubmitSuccess(true);
      setTimeout(() => {
        setSubmitSuccess(false);
        onClose();
        setFormData({
          nome: "",
          cognome: "",
          email: "",
          telefono: "",
          azienda: "",
          noteAggiuntive: "",
        });
      }, 2000);
    } catch (err) {
      console.error("Errore nell'invio del form:", err);
      setSubmitError(
        translations.contactModal.errorMessage ||
          "Impossibile inviare il modulo. Si prega di riprovare."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const materialsByAccordion = getVisibleMaterialsByAccordion();

  return (
    <div className="ay-fixed ay-inset-0 ay-bg-black ay-bg-opacity-50 ay-flex ay-items-center ay-justify-center ay-z-50">
      <div className="ay-bg-white ay-shadow-xl ay-w-full ay-max-w-4xl ay-max-h-[90vh] ay-overflow-auto">
        <div className="ay-flex ay-justify-between ay-items-center ay-px-6 ay-py-4 ay-border-b ay-border-gray-200">
          <h2 className="ay-text-xl ay-font-bold ay-text-gray-800">
            {translations.contactModal.title}
          </h2>
          <button
            onClick={onClose}
            className="ay-text-gray-500 ay-hover:ay-text-gray-700 ay-transition-colors ay-focus:ay-outline-none"
          >
            <FaTimes />
          </button>
        </div>

        {submitSuccess ? (
          <div className="ay-text-center ay-py-10 ay-px-6">
            <div className="ay-bg-green-100 ay-text-green-700 ay-rounded-full ay-w-16 ay-h-16 ay-flex ay-items-center ay-justify-center ay-mx-auto ay-mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="ay-h-8 ay-w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="ay-text-xl ay-font-medium ay-mb-2">
              {translations.contactModal.successTitle}
            </h3>
            <p className="ay-text-gray-600">
              {translations.contactModal.successMessage}
            </p>
          </div>
        ) : (
          <>
            <div className="ay-p-6">
              <div className="ay-grid ay-grid-cols-1 lg:ay-grid-cols-2 ay-gap-6">
                {/* LEFT COLUMN - Product and Materials Info */}
                <div>
                  {/* Screenshot Preview */}
                  {screenshotDataUrl && (
                    <div className="ay-mb-6 ay-bg-gray-50 ay-border ay-border-gray-200 ay-p-4 ay-rounded">
                      <h4 className="ay-font-medium ay-text-gray-800 ay-mb-2 ay-text-sm">
                        {language === "it"
                          ? "Anteprima Configurazione"
                          : language === "en"
                          ? "Configuration Preview"
                          : language === "fr"
                          ? "Aperçu de la configuration"
                          : "Konfigurationsvorschau"}
                      </h4>
                      <img
                        src={screenshotDataUrl}
                        alt="Preview configurazione"
                        className="ay-w-full ay-h-auto ay-rounded ay-border ay-border-gray-300"
                      />
                    </div>
                  )}

                  {/* Product Information */}
                  <div className="ay-mb-6">
                    <h3 className="ay-font-bold ay-text-gray-800 ay-mb-3">
                      {translations.contactModal.productDetails}
                    </h3>
                    <div className="ay-p-4 ay-bg-gray-50 ay-border ay-border-gray-200">
                      <div className="ay-space-y-2">
                        <div className="ay-flex ay-justify-between">
                          <span className="ay-text-gray-600 ay-font-medium">
                            {translations.model}:
                          </span>
                          <span className="ay-font-medium">
                            {selectedProduct?.name}
                          </span>
                        </div>
                        <div className="ay-flex ay-justify-between">
                          <span className="ay-text-gray-600 ay-font-medium">
                            {translations.code}:
                          </span>
                          <span className="ay-font-medium">
                            {selectedProduct?.code}
                          </span>
                        </div>
                        {(selectedProduct?.family ||
                          selectedProduct?._family) && (
                          <div className="ay-flex ay-justify-between">
                            <span className="ay-text-gray-600 ay-font-medium">
                              {translations.family}:
                            </span>
                            <span className="ay-font-medium">
                              {selectedProduct?.family ||
                                selectedProduct?._family}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Price Information */}
                  <div className="ay-mb-6 ay-bg-gray-50 ay-border ay-border-gray-200 ay-overflow-hidden">
                    <div className="ay-p-4 ay-border-b ay-border-gray-200">
                      <div className="ay-flex ay-justify-between ay-items-center">
                        <h3 className="ay-font-bold ay-text-gray-800">
                          {translations.priceInfo.estimate}
                        </h3>
                        <div className="ay-text-right">
                          <p className="ay-text-sm ay-text-gray-600 ay-mb-1">
                            {translations.priceInfo.startingFrom}
                          </p>
                          <p className="ay-text-xl ay-font-bold ay-text-gray-800">
                            €{(selectedProduct?.price || 0).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="ay-p-4 ay-bg-blue-50 ay-border-b ay-border-gray-200">
                      <h4 className="ay-font-medium ay-text-gray-800 ay-mb-2">
                        {translations.priceInfo.contactInfo}
                      </h4>
                      <div className="ay-space-y-1 ay-text-sm">
                        <p className="ay-text-gray-600">
                          <span className="ay-font-medium">Tel:</span>{" "}
                          {translations.priceInfo.phone}
                        </p>
                        <p className="ay-text-gray-600">
                          <span className="ay-font-medium">Email:</span>{" "}
                          {translations.priceInfo.emailSales}
                        </p>
                        <p className="ay-text-gray-600">
                          <span className="ay-font-medium">Marketing:</span>{" "}
                          {translations.priceInfo.emailMarketing}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Selected Materials */}
                  {Object.keys(materialsByAccordion).length > 0 && (
                    <div className="ay-mb-6">
                      <h3 className="ay-font-bold ay-text-gray-800 ay-mb-3">
                        {translations.contactModal.selectedMaterials}
                      </h3>

                      <div className="ay-bg-white ay-border ay-border-gray-200 ay-overflow-hidden">
                        {Object.entries(materialsByAccordion).map(
                          ([accordionName, materials]) => (
                            <div
                              key={accordionName}
                              className="ay-border-b ay-border-gray-200 ay-last:ay-border-b-0"
                            >
                              <div className="ay-bg-gray-50 ay-p-3 ay-border-b ay-border-gray-200">
                                <h4 className="ay-font-medium ay-text-gray-800">
                                  {accordionName}
                                </h4>
                              </div>
                              <div className="ay-divide-y ay-divide-gray-100">
                                {materials.map((item, index) => (
                                  <div
                                    key={`${item.type}-${index}`}
                                    className="ay-p-3 ay-flex ay-items-center ay-gap-3"
                                  >
                                    {item.material.thumbnail &&
                                    item.material.thumbnail.startsWith("#") ? (
                                      <div
                                        className="ay-w-8 ay-h-8 ay-rounded-full ay-border ay-border-gray-300 ay-flex-shrink-0"
                                        style={{
                                          backgroundColor:
                                            item.material.thumbnail,
                                        }}
                                      ></div>
                                    ) : (
                                      <img
                                        src={getImageUrl(
                                          item.material.thumbnail
                                        )}
                                        alt={item.material.name}
                                        className="ay-w-8 ay-h-8 ay-rounded-full ay-object-cover ay-border ay-border-gray-300 ay-flex-shrink-0"
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.src =
                                            'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect width="100" height="100" fill="%23cccccc"/></svg>';
                                        }}
                                      />
                                    )}
                                    <div className="ay-flex-1">
                                      <p className="ay-font-medium">
                                        {item.material.name}
                                      </p>
                                      {item.material.collection && (
                                        <p className="ay-text-xs ay-text-gray-500">
                                          {item.material.collection}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* RIGHT COLUMN - Contact Form */}
                <div>
                  <form onSubmit={handleSubmit}>
                    <h3 className="ay-font-bold ay-text-gray-800 ay-mb-3">
                      {translations.contactModal.personalData}
                    </h3>

                    <div className="ay-grid ay-grid-cols-1 ay-gap-4 md:ay-grid-cols-2">
                      <div>
                        <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                          {translations.contactModal.name}
                        </label>
                        <input
                          type="text"
                          name="nome"
                          value={formData.nome}
                          onChange={handleChange}
                          className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                          {translations.contactModal.surname}
                        </label>
                        <input
                          type="text"
                          name="cognome"
                          value={formData.cognome}
                          onChange={handleChange}
                          className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                          {translations.contactModal.email}
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                          {translations.contactModal.phone}
                        </label>
                        <input
                          type="tel"
                          name="telefono"
                          value={formData.telefono}
                          onChange={handleChange}
                          className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                        />
                      </div>

                      <div className="md:ay-col-span-2">
                        <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                          {translations.contactModal.company}
                        </label>
                        <input
                          type="text"
                          name="azienda"
                          value={formData.azienda}
                          onChange={handleChange}
                          className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                        />
                      </div>

                      <div className="md:ay-col-span-2">
                        <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                          {language === "it"
                            ? "Riassunto Prodotto"
                            : language === "en"
                            ? "Product Summary"
                            : language === "fr"
                            ? "Résumé du produit"
                            : "Produktzusammenfassung"}
                        </label>
                        <textarea
                          value={generateProductSummary()}
                          readOnly
                          rows={8}
                          className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-bg-gray-50 ay-text-gray-700 ay-text-sm"
                        ></textarea>
                      </div>

                      <div className="md:ay-col-span-2">
                        <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                          {translations.contactModal.additionalNotes}
                        </label>
                        <textarea
                          name="noteAggiuntive"
                          value={formData.noteAggiuntive}
                          onChange={handleChange}
                          rows={4}
                          className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                          placeholder={
                            translations.contactModal.additionalNotesPlaceholder
                          }
                        ></textarea>
                      </div>
                    </div>

                    {submitError && (
                      <div className="ay-mt-4 ay-p-3 ay-bg-red-50 ay-text-red-700 ay-rounded-md ay-text-sm">
                        {submitError}
                      </div>
                    )}

                    {isGeneratingAttachments && (
                      <div className="ay-mt-4 ay-p-3 ay-bg-blue-50 ay-text-blue-700 ay-rounded-md ay-text-sm ay-flex ay-items-center ay-gap-2">
                        <svg
                          className="ay-animate-spin ay-h-4 ay-w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="ay-opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="ay-opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        {language === "it"
                          ? "Generazione allegati in corso..."
                          : language === "en"
                          ? "Generating attachments..."
                          : language === "fr"
                          ? "Génération des pièces jointes..."
                          : "Anhänge werden generiert..."}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>

            {/* Link Privacy */}
            <div className="ay-px-6 ay-pb-4">
              <div className="ay-text-sm ay-text-gray-600 ay-text-center">
                <p>
                  {language === "it"
                    ? "Inviando questo modulo, accetti la nostra"
                    : language === "en"
                    ? "By submitting this form, you accept our"
                    : language === "fr"
                    ? "En soumettant ce formulaire, vous acceptez notre"
                    : "Mit dem Absenden dieses Formulars akzeptieren Sie unsere"}{" "}
                  <a
                    href="https://www.dieffebi.com/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ay-text-black ay-underline ay-hover:ay-text-gray-700"
                  >
                    {language === "it"
                      ? "Informativa Privacy"
                      : language === "en"
                      ? "Privacy Policy"
                      : language === "fr"
                      ? "Politique de confidentialité"
                      : "Datenschutzerklärung"}
                  </a>
                </p>
              </div>
            </div>

            {/* Footer buttons */}
            <div className="ay-px-6 ay-py-4 ay-bg-gray-50 ay-border-t ay-border-gray-200 ay-flex ay-justify-end ay-gap-3">
              <button
                type="button"
                onClick={onClose}
                className="ay-text-[12px] ay-text-center 3xl:ay-text-[16px] ay-min-w-36 ay-border ay-border-black ay-px-6 ay-py-1 ay-bg-white ay-text-black hover:ay-bg-black hover:ay-text-white disabled:ay-opacity-50 ay-transition-colors ay-focus:ay-outline-none"
                disabled={isSubmitting || isGeneratingAttachments}
              >
                {translations.contactModal.cancel}
              </button>
              <button
                onClick={handleSubmit}
                className="ay-text-[12px] ay-text-center 3xl:ay-text-[16px] ay-min-w-36 ay-border ay-border-black ay-px-6 ay-py-1 ay-bg-black ay-text-white hover:ay-bg-white hover:ay-text-black disabled:ay-opacity-50 ay-flex ay-items-center ay-justify-center ay-gap-2 ay-transition-colors ay-focus:ay-outline-none"
                disabled={isSubmitting || isGeneratingAttachments}
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="ay-animate-spin ay-h-4 ay-w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="ay-opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="ay-opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    {translations.contactModal.submitting}
                  </>
                ) : (
                  translations.contactModal.submit
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ContactModal;
